import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import type { JobTaskView } from '@codebox/shared';
import { AiAvatar } from './AiAvatar';
import { TaskListPanel } from './TaskListPanel';
import { TokenMeter, type TokenState } from './TokenMeter';

export interface TrailEntry {
  id: string;
  userId: string;
  role: 'build' | 'edit' | 'plan' | 'feedback' | 'survey';
  promptText: string;
  outcome: 'ok' | 'error' | 'cancelled';
  versionId: string | null;
  modelTier: string | null;
  /** 35i #4: present on snapshot-feedback entries — renders a custom bubble instead of the raw text */
  metrics?: { snapshotFeedback?: { imageUrl: string; comments: string[] } } | null;
  createdAt: string;
}

/** A file the learner attached as reference material for the AI (35e). */
export interface Attachment {
  name: string;
  mimeType: string;
  /** full data: URL — the parent extracts base64 to send */
  dataUrl: string;
}

/** The element the learner picked with the Select tool (35g), shown as a chip on the input. */
export interface SelectedElementChip {
  tag: string;
  id: string;
  cls: string;
  text: string;
}

/** The single active background task, shown inside the thread (35i #4) with a square Stop button. */
export interface ThreadWork {
  label: string;
  /** one line of context — for auto-fixes, the diagnosis that triggered them */
  detail?: string | null;
  /** the running job's checklist — only for the n/total counter on the bubble */
  tasks?: JobTaskView[];
  /** whether the floating plan panel is currently open */
  tasksOpen?: boolean;
  /** toggle the floating plan panel; absent means the bubble is not a trigger */
  onToggleTasks?: () => void;
  pct: number;
  indeterminate: boolean;
  waiting: number;
  onStop: () => void;
}

interface ChatPanelProps {
  entries: TrailEntry[];
  busy: boolean;
  canPinModel: boolean;
  meId?: string;
  onSend: (prompt: string, mode: 'build' | 'plan' | 'debug', modelTier?: string, attachments?: Attachment[]) => Promise<void>;
  /** the balance, for the meter beside the send button; null while it loads */
  tokens?: TokenState | null;
  /** 35g: element selected in the preview — the next prompt targets it */
  selected?: SelectedElementChip | null;
  /** clear the current selection */
  onClearSelected?: () => void;
  /** 35i #4: the shared background task (build/edit/fix/care check), rendered in the thread */
  work?: ThreadWork | null;
  /** whether the app has been built yet — drives which suggestions make sense */
  hasBuild?: boolean;
}

const START_SUGGESTIONS = ['Make it more colorful', 'Add a button', 'Make the text bigger', 'Change the background'];

/** 35i #6: suggestions adapt to the conversation — usually the next thing worth doing. */
function suggestionsFor(hasBuild: boolean, lastAiText: string | null): string[] {
  if (!hasBuild) return ['Build the first version', 'Add more detail to my idea', 'Start simple'];
  const t = (lastAiText ?? '').toLowerCase();
  const pool: string[] = [];
  if (/\b(game|play|score|level|player|enemy|win|lose)\b/.test(t)) pool.push('Make it harder', 'Add a score counter', 'Add sound effects', 'Add a start screen');
  if (/\b(color|color|background|theme|style|look)\b/.test(t)) pool.push('Try a different color theme', 'Make it look more modern');
  if (/\b(button|form|input|click|tap)\b/.test(t)) pool.push('Add another button', 'Make the buttons bigger');
  if (/\b(mobile|phone|responsive|screen)\b/.test(t)) pool.push('Make it work better on phones');
  // always-useful next steps to round out the set
  pool.push('Add a title screen', 'Improve the layout', 'Add an animation', 'Make it more colorful');
  return [...new Set(pool)].slice(0, 4);
}

/* ---- small inline icons (ported from Sparked v2's co-author) ---- */
function PaperclipIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05 12.25 20.24a5 5 0 0 1-7.07-7.07l9.19-9.19a3 3 0 0 1 4.24 4.24l-9.2 9.19a1 1 0 0 1-1.41-1.41l8.49-8.49" />
    </svg>
  );
}
function MicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0M12 17v4M8 21h8" />
    </svg>
  );
}
function SendArrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}
function XIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
function Dot() {
  return <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted [&:nth-child(2)]:[animation-delay:-0.1s] [&:nth-child(3)]:[animation-delay:-0.2s]" />;
}

/** 35i #4: the shared background task shown IN the thread, with a square-icon Stop button. */
/**
 * The running-work bubble. It is the TRIGGER for the checklist, not its container.
 *
 * The plan lives in a floating panel over the workspace (StudioPage), expanded by default. Rendering
 * it inside this bubble made a long plan push the conversation around and trapped it inside the
 * rail's width; keeping it outside means the steps stay readable while the thread stays a thread.
 */
function WorkRow({ work }: { work: ThreadWork }) {
  const tasks = work.tasks ?? [];
  const canOpen = tasks.length > 0 && Boolean(work.onToggleTasks);
  const open = Boolean(work.tasksOpen);
  const done = tasks.filter(t => t.status !== 'planned' && t.status !== 'running').length;

  return (
    <div className="anim-in flex justify-start">
      <div className="w-full max-w-[85%] rounded-xl rounded-bl-sm bg-page px-3 py-2.5">
        {/*
          A div, not a button: Stop lives inside this row and nesting interactive elements is invalid
          and breaks keyboard use. Role + key handling give it the same affordances.
        */}
        <div
          {...(canOpen
            ? {
                role: 'button' as const,
                tabIndex: 0,
                'aria-expanded': open,
                title: open ? 'Hide the plan' : 'Show the plan',
                onClick: () => work.onToggleTasks?.(),
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  work.onToggleTasks?.();
                },
              }
            : {})}
          className={['flex items-center gap-2 rounded-lg', canOpen ? 'cursor-pointer' : ''].join(' ')}
        >
          <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-accent" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-semibold text-ink">{work.label}</span>
            {work.detail && <span className="block truncate text-[11px] text-muted">{work.detail}</span>}
          </span>
          {canOpen && (
            <span className="shrink-0 text-[11px] font-semibold text-muted">
              {done}/{tasks.length} {open ? '▾' : '▸'}
            </span>
          )}
          <button
            type="button"
            onClick={e => {
              e.stopPropagation(); // never let Stop toggle the drawer
              work.onStop();
            }}
            title="Stop"
            aria-label="Stop"
            className="flex shrink-0 items-center gap-1 rounded-lg border border-line bg-surface px-2 py-0.5 text-[11px] font-semibold text-muted transition hover:text-ink"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="5" y="5" width="14" height="14" rx="2.5" />
            </svg>
            Stop
          </button>
        </div>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-surface">
          <div
            className={work.indeterminate ? 'h-full w-1/3 animate-pulse rounded-full bg-accent' : 'h-full rounded-full bg-accent transition-all duration-500'}
            style={work.indeterminate ? undefined : { width: `${Math.max(6, work.pct)}%` }}
          />
        </div>
        {work.waiting > 0 && (
          <p className="mt-1 text-[11px] text-muted">
            {work.waiting} more {work.waiting === 1 ? 'request' : 'requests'} waiting in line
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Repair asset URLs already stored in the thread history.
 *
 * The server used to build `https://<bucket-with-dots>.s3.<region>.amazonaws.com/…` for snapshot
 * images — a form that can never load, because the S3 wildcard cert covers one label and the
 * dotted bucket name fails the TLS handshake. The server is fixed, but every snapshot bubble saved
 * before the fix carries the dead URL in its metrics forever. Rewriting at render time heals all of
 * history with no data migration: the bucket is named after the domain that serves it, so the fix
 * is dropping the S3 suffix.
 */
const fixAssetUrl = (url: string): string =>
  url.replace(/^https:\/\/([^/]*\.[^/]*)\.s3\.[a-z0-9-]+\.amazonaws\.com\//, 'https://$1/');

const initialsOf = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase() || '??';
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

interface Msg {
  id: string;
  kind: 'user' | 'sparky' | 'survey' | 'snapshot';
  who: string;
  mine: boolean;
  text: string;
  createdAt: string;
  outcome: TrailEntry['outcome'];
  /** survey kind only: the Q→A pairs */
  qa?: Array<{ q: string; a: string }>;
  /** snapshot kind only: the flattened annotated image + comment lines */
  snap?: { imageUrl: string; comments: string[] };
}

/**
 * AI Collaboration thread (spec 35e) — one shared conversation for every collaborator, styled to match
 * Sparked v2's Sparky co-author (animated orb greeting, chat bubbles, chips, a clean input bar).
 *
 * While the AI is working the whole thread is disabled for EVERYONE (`busy` derives from the shared job
 * list). Survey answers collapse into a single custom "survey" bubble. Files can be attached as
 * reference material.
 */
export function ChatPanel({ entries, busy, canPinModel, meId, onSend, selected, onClearSelected, work, hasBuild = false, tokens = null }: ChatPanelProps) {
  const [draft, setDraft] = useState('');
  const [mode, setMode] = useState<'build' | 'plan' | 'debug'>('build');
  /* Only BUILD spends. Plan and debug stay available on an empty balance on purpose: a learner who
     has run out should still be able to think, read and test what they already made. */
  const outOfTokens = Boolean(tokens?.enforced && tokens.balance <= 0 && mode === 'build');
  const [modelTier, setModelTier] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showSugg, setShowSugg] = useState(true);
  const [lightbox, setLightbox] = useState<{ imageUrl: string; comments: string[] } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // fold the trail into chat messages; consecutive survey rows become ONE bubble (35e item 5)
  const messages = useMemo<Msg[]>(() => {
    const out: Msg[] = [];
    for (const e of entries) {
      if (e.role === 'survey') {
        const [q, a] = e.promptText.split('->').map(s => s.trim());
        const pair = { q: q ?? e.promptText, a: a ?? '' };
        const last = out[out.length - 1];
        if (last && last.kind === 'survey') {
          last.qa!.push(pair);
          continue;
        }
        out.push({ id: e.id, kind: 'survey', who: 'Sparky', mine: false, text: '', createdAt: e.createdAt, outcome: e.outcome, qa: [pair] });
        continue;
      }
      const snap = e.metrics?.snapshotFeedback;
      if (snap?.imageUrl) {
        out.push({
          id: e.id,
          kind: 'snapshot',
          who: e.userId === meId ? 'You' : 'Collaborator',
          mine: e.userId === meId,
          text: '',
          createdAt: e.createdAt,
          outcome: e.outcome,
          snap: { imageUrl: snap.imageUrl, comments: snap.comments ?? [] },
        });
        continue;
      }
      const isSparky = e.userId === 'sparky';
      out.push({
        id: e.id,
        kind: isSparky ? 'sparky' : 'user',
        who: isSparky ? 'Sparky' : e.userId === meId ? 'You' : 'Collaborator',
        mine: e.userId === meId,
        text: e.promptText,
        createdAt: e.createdAt,
        outcome: e.outcome,
      });
    }
    return out;
  }, [entries, meId]);

  const hasThread = messages.length > 0;

  // 35i #6: dynamic suggestions from the most recent Sparky/AI message + whether a build exists
  const suggestions = useMemo(() => {
    const lastAi = [...messages].reverse().find(m => m.kind === 'sparky' || m.kind === 'survey');
    return hasThread ? suggestionsFor(hasBuild, lastAi?.text ?? null) : START_SUGGESTIONS;
  }, [messages, hasBuild, hasThread]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, busy]);

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).slice(0, 5);
    void Promise.all(
      files.map(
        f =>
          new Promise<Attachment>((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve({ name: f.name, mimeType: f.type, dataUrl: String(r.result) });
            r.onerror = reject;
            r.readAsDataURL(f);
          }),
      ),
    ).then(list => setAttachments(a => [...a, ...list].slice(0, 5)));
    e.target.value = '';
  };

  const submit = async () => {
    const prompt = draft.trim();
    if ((!prompt && attachments.length === 0) || busy) return;
    const files = attachments;
    setDraft('');
    setAttachments([]);
    await onSend(prompt || 'Use the attached reference.', mode, modelTier || undefined, files.length ? files : undefined);
  };

  const dispatch = (text: string) => {
    if (busy) return;
    void onSend(text, mode, modelTier || undefined);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* 35i #4: snapshot lightbox — the flattened image (doodles + numbered pins) + the comments */}
      {lightbox && <SnapshotLightbox data={lightbox} onClose={() => setLightbox(null)} />}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {/* ONE persistent avatar — centerd in the empty state, animates to the corner once a thread exists */}
        <div
          className="pointer-events-none absolute z-10 transition-all duration-500 ease-out"
          style={{
            top: hasThread ? '10px' : '40%',
            left: hasThread ? '12px' : '50%',
            transform: `translate(${hasThread ? '0' : '-50%'}, ${hasThread ? '0px' : '-50%'}) scale(${hasThread ? 0.55 : 1})`,
            transformOrigin: 'top left',
          }}
        >
          <AiAvatar speaking={busy} size={96} />
        </div>

        {/* empty-state greeting + suggestions */}
        <div
          className={`absolute inset-x-0 px-5 text-center transition-opacity duration-300 ${hasThread ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
          style={{ top: '56%' }}
        >
          <p className="text-sm font-bold text-ink">Let&rsquo;s build this together</p>
          <p className="mx-auto mt-1 max-w-[17rem] text-xs text-muted">
            Ask for a change, attach a reference, or talk to me. Everyone on this project shares this
            thread.
          </p>
          <div className="mt-3 flex flex-wrap justify-center gap-1.5">
            {suggestions.map(s => (
              <button
                key={s}
                type="button"
                onClick={() => dispatch(s)}
                disabled={busy}
                className="rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-ink transition hover:border-accent/50 hover:text-accent-deep disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* the thread */}
        <div
          ref={scrollRef}
          className={`absolute inset-0 space-y-3 overflow-auto px-3 transition-opacity duration-500 ${hasThread ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          style={{
            paddingTop: 74,
            paddingBottom: 14,
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, #000 28px, #000 calc(100% - 16px), transparent 100%)',
            maskImage: 'linear-gradient(to bottom, transparent 0, #000 28px, #000 calc(100% - 16px), transparent 100%)',
          }}
        >
          {messages.map(m =>
            m.kind === 'survey' ? (
              <SurveyBubble key={m.id} msg={m} />
            ) : m.kind === 'snapshot' ? (
              <SnapshotBubble key={m.id} msg={m} onOpen={() => m.snap && setLightbox(m.snap)} />
            ) : (
              <Bubble key={m.id} msg={m} />
            ),
          )}
          {work ? (
            <WorkRow work={work} />
          ) : (
            busy && (
              <div className="flex justify-start">
                <div className="flex items-center gap-1 rounded-2xl bg-page px-3 py-2.5">
                  <Dot />
                  <Dot />
                  <Dot />
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {/* input area */}
      <div className="border-t border-line p-3">
        {busy && (
          <div className="anim-in mb-2 flex items-center gap-2 rounded-xl border border-accent/40 bg-accent/10 px-2.5 py-1.5 text-[11px] font-semibold text-accent-deep">
            <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-accent/40 border-t-accent" />
            Sparky is working — the thread is paused for everyone
          </div>
        )}

        {/* collapsible suggestions once the thread has started */}
        {hasThread && !draft.trim() && (
          <div className="mb-2.5">
            <button
              type="button"
              onClick={() => setShowSugg(s => !s)}
              className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-muted transition hover:text-ink"
            >
              <span className={`inline-flex transition-transform duration-300 ${showSugg ? '' : '-rotate-90'}`}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </span>
              Suggestions
            </button>
            <div className={`grid transition-all duration-300 ease-out ${showSugg ? 'grid-rows-[1fr] pt-2 opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
              <div className="overflow-hidden">
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => dispatch(s)}
                      disabled={busy}
                      className="rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-muted transition hover:border-accent/50 hover:text-accent-deep disabled:opacity-50"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* mode + model (compact) */}
        <div className="mb-2 flex items-center gap-2">
          <div className="inline-flex rounded-lg bg-page/70 p-0.5">
            {(['build', 'plan', 'debug'] as const).map(m => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                disabled={busy}
                className={[
                  'rounded-md px-3 py-1 text-xs font-semibold capitalize transition disabled:opacity-50',
                  mode === m ? 'bg-sun text-brand shadow-sm' : 'text-muted hover:text-ink',
                ].join(' ')}
              >
                {m}
              </button>
            ))}
          </div>
          {canPinModel && (
            <select
              value={modelTier}
              onChange={e => setModelTier(e.target.value)}
              disabled={busy}
              className="rounded-lg border border-line bg-surface px-2 py-1 text-xs text-muted disabled:opacity-50"
              aria-label="Model"
            >
              <option value="">Auto</option>
              <option value="fast">Fast</option>
              <option value="default">Standard</option>
              <option value="premium">Premium</option>
            </select>
          )}
          {/* Plan and debug are free (D8), so the meter says so rather than quoting a price. */}
          <TokenMeter
            tokens={tokens}
            estimate={
              mode !== 'build'
                ? null
                : hasBuild
                  ? (tokens?.estimates.edit ?? null)
                  : (tokens?.estimates.generate ?? null)
            }
            className="ml-auto"
          />
          <span className="truncate text-[11px] text-muted">
            {mode === 'plan' ? 'Talk it through — planning is free.' : mode === 'debug' ? 'Describe the problem — I will read the console errors too.' : 'I will change your app.'}
          </span>
        </div>

        {/* selected-element chip (35g) — the Select tool picked a target for the next prompt */}
        {selected && (
          <div className="mb-2 flex items-center gap-1.5 rounded-lg border border-accent/40 bg-accent/10 px-2 py-1 text-[11px] text-accent-deep">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M6 3l14 8-6 1.5L12 20 6 3z" />
            </svg>
            <span className="min-w-0 truncate font-semibold">
              Editing&nbsp;
              <span className="font-mono">
                {selected.text ? `"${selected.text.slice(0, 32)}"` : `<${selected.tag}${selected.id ? `#${selected.id}` : ''}>`}
              </span>
            </span>
            {onClearSelected && (
              <button type="button" onClick={onClearSelected} aria-label="Clear selection" className="ml-auto shrink-0 hover:text-ink">
                <XIcon />
              </button>
            )}
          </div>
        )}

        {/* attachment chips */}
        {attachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {attachments.map((f, i) => (
              <span key={i} className="flex items-center gap-1 rounded-lg border border-line bg-page px-2 py-1 text-[11px]">
                <span className="max-w-[9rem] truncate">{f.name}</span>
                <button type="button" onClick={() => setAttachments(a => a.filter((_, j) => j !== i))} aria-label="Remove" className="text-muted hover:text-ink">
                  <XIcon />
                </button>
              </span>
            ))}
          </div>
        )}

        <input ref={fileRef} type="file" multiple accept="image/*,application/pdf,.doc,.docx" className="hidden" onChange={onPick} />
        <div className="rounded-2xl border border-line bg-page p-2 transition focus-within:border-accent/60">
          <textarea
            rows={2}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
            disabled={busy}
            placeholder={busy ? 'Sparky is working…' : mode === 'plan' ? 'What are you thinking about?' : 'Ask, attach a file, or talk to the co-author…'}
            className="block w-full resize-none border-0 bg-transparent px-1 py-0.5 text-sm text-ink outline-none placeholder:text-muted disabled:opacity-60"
          />
          <div className="mt-1 flex items-center justify-between">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              aria-label="Attach a reference file"
              title="Attach a reference (image, PDF, Word)"
              className="flex h-8 w-8 items-center justify-center rounded-full text-muted transition hover:bg-surface hover:text-accent-deep disabled:opacity-50"
            >
              <PaperclipIcon />
            </button>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled
                aria-label="Voice input (coming soon)"
                title="Voice input — coming soon"
                className="flex h-8 w-8 items-center justify-center rounded-full text-muted opacity-40"
              >
                <MicIcon />
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={busy || outOfTokens || (draft.trim().length === 0 && attachments.length === 0)}
                aria-label="Send"
                title={outOfTokens ? 'Out of Tokens — Plan and Debug are still free' : undefined}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-ink transition hover:opacity-90 disabled:opacity-40"
              >
                <SendArrow />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- message bubbles ---- */

function Bubble({ msg }: { msg: Msg }) {
  const { mine, kind } = msg;
  const bubble = mine
    ? 'rounded-br-sm bg-accent text-accent-ink'
    : kind === 'sparky'
      ? 'rounded-bl-sm bg-page text-ink'
      : 'rounded-bl-sm bg-accent/15 text-ink';
  const tail = mine ? '-right-[4px] bg-accent' : kind === 'sparky' ? '-left-[4px] bg-page' : '-left-[4px] bg-accent/15';
  return (
    <div className={`anim-in flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
      <span className="mb-1 flex items-center gap-1.5 px-1 text-[10px] text-muted">
        {!mine && kind === 'user' && (
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent/20 text-[7px] font-bold uppercase text-accent-deep">
            {initialsOf(msg.who)}
          </span>
        )}
        <span className="font-semibold text-ink/70">{msg.who}</span> · {fmtTime(msg.createdAt)}
        {msg.outcome === 'error' && <span className="text-danger">· didn&rsquo;t work</span>}
        {msg.outcome === 'cancelled' && <span>· stopped</span>}
      </span>
      <div className={`relative max-w-[90%] whitespace-pre-line rounded-2xl px-3 py-2 text-sm ${bubble}`}>
        {msg.text}
        <span className={`absolute bottom-[5px] h-3 w-3 rotate-45 rounded-[2px] ${tail}`} aria-hidden />
      </div>
    </div>
  );
}

/** 35i #4: the learner's snapshot feedback — a "Snapshot Feedbacks" bubble with a clickable thumbnail. */
function SnapshotBubble({ msg, onOpen }: { msg: Msg; onOpen: () => void }) {
  if (!msg.snap) return null;
  return (
    <div className={`anim-in flex flex-col ${msg.mine ? 'items-end' : 'items-start'}`}>
      <span className="mb-1 flex items-center gap-1.5 px-1 text-[10px] text-muted">
        <span className="font-semibold text-ink/70">{msg.who}</span> · {fmtTime(msg.createdAt)}
      </span>
      <div className="w-[220px] max-w-[90%] overflow-hidden rounded-2xl rounded-br-sm border border-accent/30 bg-accent/10">
        <p className="px-3 pb-1.5 pt-2 text-[11px] font-bold text-accent-deep">Snapshot Feedbacks</p>
        <button type="button" onClick={onOpen} className="group block w-full" title="Open snapshot with notes">
          <span className="relative block">
            <img
              src={fixAssetUrl(msg.snap.imageUrl)}
              alt="Snapshot feedback"
              className="block min-h-[72px] w-full bg-page object-cover"
              onError={e => {
                /* a genuinely missing object must not render the browser's broken-image glyph */
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement?.insertAdjacentHTML(
                  'afterbegin',
                  '<span class="grid min-h-[72px] w-full place-items-center text-[11px] text-muted">Snapshot unavailable</span>',
                );
              }}
            />
            <span className="absolute inset-0 grid place-items-center bg-ink/0 transition group-hover:bg-ink/25">
              <span className="rounded-full bg-surface/90 px-2.5 py-1 text-[11px] font-semibold text-ink opacity-0 shadow transition group-hover:opacity-100">View notes</span>
            </span>
          </span>
        </button>
        {msg.snap.comments.length > 0 && (
          <p className="px-3 py-1.5 text-[11px] text-ink/70">
            {msg.snap.comments.length} {msg.snap.comments.length === 1 ? 'note' : 'notes'} — click to read
          </p>
        )}
      </div>
    </div>
  );
}

/** Full-screen viewer for a snapshot: the flattened image (doodles + numbered pins) beside its notes. */
/**
 * Notes are stored as display strings ("1. (top-center) admin@demo.local: make this green").
 * Pull the parts back out so the viewer can typeset them: the REQUEST is the content, the position
 * and author are quiet metadata underneath. Anything that does not match renders as-is.
 */
function parseNote(line: string): { n: string; position?: string; author?: string; text: string } {
  const m = /^(\d+)\.\s*\(([^)]*)\)\s*(.*)$/.exec(line);
  if (!m) return { n: '•', text: line };
  const rest = m[3];
  const a = /^([^\s:]+@[^\s:]+):\s*(.*)$/.exec(rest);
  return { n: m[1], position: m[2], author: a?.[1], text: a ? a[2] : rest };
}

function SnapshotLightbox({ data, onClose }: { data: { imageUrl: string; comments: string[] }; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const notes = data.comments.map(parseNote);
  return (
    /* One coherent surface, matching the studio's design language — the old version floated a
       stark white card over a blurred page and let the image drift in a void of margin. */
    <div className="fixed inset-0 z-[60] flex flex-col bg-surface" role="dialog" aria-modal aria-label="Snapshot feedback">
      <div className="flex shrink-0 items-center justify-between px-5 py-3">
        <span className="text-sm font-bold text-ink">Snapshot feedback</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink"
        >
          <XIcon />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-5 pb-5 lg:flex-row">
        {/* the image owns the space, on a stage that makes app screenshots read as screens */}
        <div className="flex min-h-0 flex-[3] items-center justify-center overflow-hidden rounded-card bg-ink/95 p-4">
          <img src={fixAssetUrl(data.imageUrl)} alt="Snapshot with notes" className="max-h-full max-w-full rounded-lg" />
        </div>
        {notes.length > 0 && (
          <div className="min-h-0 shrink-0 overflow-auto lg:w-[300px]">
            <p className="mb-2 text-sm font-bold text-ink">Notes</p>
            <ul className="space-y-2">
              {notes.map((note, i) => (
                <li key={i} className="rounded-field bg-page/70 px-3 py-2.5">
                  <div className="flex items-start gap-2.5">
                    {/* numbered chip in the same color as the pin on the image — the visual join */}
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#2b62b8] text-[11px] font-bold text-white">
                      {note.n}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-ink">{note.text}</p>
                      {(note.position || note.author) && (
                        <p className="mt-0.5 truncate text-[11px] text-muted">
                          {[note.position && `pin at ${note.position}`, note.author].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/** Custom bubble for a whole set of survey answers (35e item 5). */
function SurveyBubble({ msg }: { msg: Msg }) {
  return (
    <div className="anim-in flex flex-col items-start">
      <span className="mb-1 flex items-center gap-1.5 px-1 text-[10px] text-muted">
        <span className="font-semibold text-ink/70">Your answers</span> · {fmtTime(msg.createdAt)}
      </span>
      <div className="relative max-w-[92%] overflow-hidden rounded-2xl rounded-bl-sm border border-accent/30 bg-gradient-to-br from-accent/10 to-page">
        <div className="border-b border-accent/20 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-accent-deep">
          A few quick questions
        </div>
        <ul className="space-y-2 p-3">
          {msg.qa!.map((pair, i) => (
            <li key={i}>
              <p className="text-[11px] text-muted">{pair.q}</p>
              <p className="text-sm font-semibold text-ink">{pair.a}</p>
            </li>
          ))}
        </ul>
        <span className="absolute -left-[4px] bottom-[5px] h-3 w-3 rotate-45 rounded-[2px] border-b border-l border-accent/30 bg-accent/10" aria-hidden />
      </div>
    </div>
  );
}
